/**
 * Report Builder - Place Chart JavaScript
 *
 * Manages chart placement on the report canvas
 */

(function () {
  "use strict";

  // Store placed charts
  let placedCharts = [];
  let chartCounter = 0;
  let currentChartConfigPlaceholder = null;
  let isAddingChartPlaceholder = false;

  /**
   * Function to get tracked responses data from PHP (lazy loading)
   */
  function getTrackedResponsesData() {
    return window.reportBuilderData?.trackedResponses || [];
  }

  /**
   * Chart AI summary integration
   *
   * Chart templates store the final "data used to build the chart" in:
   * window.MchriChartAi.charts[chartContainerId]
   */
  function getChartAiStore() {
    if (!window.MchriChartAi) {
      window.MchriChartAi = { charts: {} };
    } else if (!window.MchriChartAi.charts) {
      window.MchriChartAi.charts = {};
    }
    return window.MchriChartAi;
  }

  function getChartAiData(chartContainerId) {
    return getChartAiStore().charts?.[chartContainerId] || null;
  }

  function formatFilterLabel(chartConfig) {
    const parts = [];
    if (chartConfig.startDate)
      parts.push(`start_date=${chartConfig.startDate}`);
    if (chartConfig.endDate) parts.push(`end_date=${chartConfig.endDate}`);
    if (chartConfig.wardName) parts.push(`ward=${chartConfig.wardName}`);
    return parts.length ? parts.join(", ") : "none";
  }

  function buildAiPrompt({ chartConfig, chartData }) {
    const safeQuestion = chartConfig?.questionText || "Unknown question";
    const safeChartType = getChartTypeLabel(chartConfig?.chartType || "");
    const filterLabel = formatFilterLabel(chartConfig);

    // Keep it structured and explicit; backend prepends "Analyze this data: "
    // so we send the rest as one string payload.
    const payload = {
      chart_type: safeChartType,
      chart_template: chartConfig?.chartType || "",
      question: safeQuestion,
      field_name: chartConfig?.fieldName || "",
      filters: {
        start_date: chartConfig?.startDate || null,
        end_date: chartConfig?.endDate || null,
        ward_name: chartConfig?.wardName || null,
      },
      data_used_to_build_chart: chartData,
    };

    return [
      "use Australian English spelling and grammar",
      "You are a reporting assistant. Write a brief summary of what this chart shows.",
      "Constraints:",
      "- 2 to 4 sentences.",
      "- Be specific about the trend/distribution and any notable outliers.",
      "- If the data is empty or indicates no submissions, say that clearly.",
      "",
      `Chart type: ${safeChartType}`,
      `Question: ${safeQuestion}`,
      `Filters applied: ${filterLabel}`,
      "",
      "Data (JSON):",
      JSON.stringify(payload, null, 2),
    ].join("\n");
  }

  async function requestAiSummary(promptString) {
    if (typeof chatgptAnalyzer === "undefined") {
      throw new Error("ChatGPT analyzer is not available on this page.");
    }

    const formData = new FormData();
    formData.append("action", "analyze_with_chatgpt");
    formData.append("nonce", chatgptAnalyzer.nonce);
    formData.append("data", promptString);

    const response = await fetch(chatgptAnalyzer.ajaxurl, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `AI request failed (HTTP ${response.status}): ${errorText}`
      );
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.data?.message || "AI request failed.");
    }

    return result.data.analysis || "";
  }

  /**
   * Initialize the place chart functionality
   */
  function initPlaceChart() {
    // This will be called from the toolbar button click
  }

  /**
   * Add chart placeholder to canvas with inline form
   */
  function addChartPlaceholder() {
    // Prevent duplicate calls
    if (isAddingChartPlaceholder) {
      return;
    }
    
    isAddingChartPlaceholder = true;
    
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      isAddingChartPlaceholder = false;
      return;
    }

    // Hide empty message
    if (window.ReportBuilderUtils?.toggleEmptyMessage) {
      window.ReportBuilderUtils.toggleEmptyMessage(false);
    }

    // Create placeholder container
    const placeholderContainer = document.createElement("div");
    placeholderContainer.className = "canvas-chart-placeholder";
    placeholderContainer.id = `chart_placeholder_${Date.now()}`;
    
    // Clone the inline form template
    const inlineFormTemplate = document.querySelector(".report-builder-place-chart-inline");
    if (!inlineFormTemplate) {
      console.error("Inline chart form template not found");
      isAddingChartPlaceholder = false;
      return;
    }

    const inlineForm = inlineFormTemplate.cloneNode(true);
    inlineForm.style.display = "block";
    inlineForm.id = `inline_form_${Date.now()}`;

    // Update IDs to be unique
    const selectQuestion = inlineForm.querySelector("#inlineSelectQuestion");
    const selectChartType = inlineForm.querySelector("#inlineSelectChartType");
    const addChartBtn = inlineForm.querySelector("#inlineAddChartBtn");
    const recommendationGroup = inlineForm.querySelector("#inlineRecommendationGroup");
    const selectRecommendation = inlineForm.querySelector("#inlineSelectRecommendation");
    
    if (selectQuestion) {
      selectQuestion.id = `inlineSelectQuestion_${Date.now()}`;
    }
    if (selectChartType) {
      selectChartType.id = `inlineSelectChartType_${Date.now()}`;
    }
    if (addChartBtn) {
      addChartBtn.id = `inlineAddChartBtn_${Date.now()}`;
    }
    if (recommendationGroup) {
      recommendationGroup.id = `inlineRecommendationGroup_${Date.now()}`;
    }
    if (selectRecommendation) {
      selectRecommendation.id = `inlineSelectRecommendation_${Date.now()}`;
    }

    placeholderContainer.appendChild(inlineForm);
    canvasCharts.appendChild(placeholderContainer);

    // Store reference to placeholder
    currentChartConfigPlaceholder = placeholderContainer;

    // Re-initialize form handlers for this instance
    initInlineChartFormInstance(inlineForm);

    // Scroll to the new placeholder
    placeholderContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });
    
    // Reset flag after a short delay to allow the operation to complete
    setTimeout(function() {
      isAddingChartPlaceholder = false;
    }, 100);
  }

  /**
   * Initialize inline chart form instance
   */
  function initInlineChartFormInstance(formElement) {
    const selectQuestion = formElement.querySelector("select[id^='inlineSelectQuestion']");
    const selectChartType = formElement.querySelector("select[id^='inlineSelectChartType']");
    const addChartBtn = formElement.querySelector("button[id^='inlineAddChartBtn']");
    const recommendationGroup = formElement.querySelector("div[id^='inlineRecommendationGroup']");
    const selectRecommendation = formElement.querySelector("select[id^='inlineSelectRecommendation']");
    const cancelButtons = formElement.querySelectorAll(".btn-cancel-chart-config");

    if (!selectQuestion || !selectChartType || !addChartBtn) {
      return;
    }

    // Handle question selection
    selectQuestion.addEventListener("change", function () {
      const selectedOption = this.options[this.selectedIndex];
      const graphStyle = selectedOption.getAttribute("data-graph-style");
      const questionIndex = parseInt(this.value);

      // Enable chart type selection
      selectChartType.disabled = false;

      // Filter available chart types based on graph_style
      const chartTypeOptions = selectChartType.querySelectorAll("option");
      chartTypeOptions.forEach((option) => {
        if (option.value === "") {
          option.style.display = "block";
          return;
        }

        // Show/hide options based on graph_style
        if (graphStyle === "bar" && option.value === "donut") {
          option.style.display = "none";
        } else if (graphStyle === "donut" && option.value === "bar") {
          option.style.display = "none";
        } else if (graphStyle === "pie" && option.value === "donut") {
          option.style.display = "none";
        } else {
          option.style.display = "block";
        }
      });

      // Reset chart type selection
      selectChartType.value = "";
      
      // Populate recommendations dropdown if available
      populateRecommendationsDropdown(questionIndex, selectRecommendation);
      
      // Hide recommendation group initially
      if (recommendationGroup) {
        recommendationGroup.style.display = "none";
      }
      if (selectRecommendation) {
        selectRecommendation.value = "";
      }
      
      updateInlineAddButtonStateInstance(selectQuestion, selectChartType, addChartBtn);
    });

    // Handle chart type selection
    selectChartType.addEventListener("change", function () {
      const chartType = this.value;
      const questionIndex = parseInt(selectQuestion.value);
      
      // Show recommendation select only for donut, bar, and traffic-light charts
      if (recommendationGroup && selectRecommendation) {
        if (chartType === "donut" || chartType === "bar" || chartType === "traffic-light") {
          recommendationGroup.style.display = "block";
          // Ensure recommendations are populated
          if (questionIndex >= 0) {
            populateRecommendationsDropdown(questionIndex, selectRecommendation);
          }
        } else {
          recommendationGroup.style.display = "none";
          selectRecommendation.value = "";
        }
      }
      
      updateInlineAddButtonStateInstance(selectQuestion, selectChartType, addChartBtn);
    });

    // Handle add chart button
    addChartBtn.addEventListener("click", function () {
      addChartFromInlineFormInstance(formElement);
    });

    // Handle cancel buttons
    cancelButtons.forEach((btn) => {
      btn.addEventListener("click", function () {
        const placeholder = formElement.closest(".canvas-chart-placeholder");
        if (placeholder) {
          placeholder.remove();
        }
        currentChartConfigPlaceholder = null;
        
        // Show empty message if no items left
        const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
        if (canvasCharts && canvasCharts.children.length === 0) {
          if (window.ReportBuilderUtils?.toggleEmptyMessage) {
            window.ReportBuilderUtils.toggleEmptyMessage(true);
          }
        }
      });
    });
  }

  /**
   * Add chart from inline form instance
   */
  function addChartFromInlineFormInstance(formElement) {
    const selectQuestion = formElement.querySelector("select[id^='inlineSelectQuestion']");
    const selectChartType = formElement.querySelector("select[id^='inlineSelectChartType']");
    const selectRecommendation = formElement.querySelector("select[id^='inlineSelectRecommendation']");
    const placeholder = formElement.closest(".canvas-chart-placeholder");

    if (!selectQuestion || !selectChartType || !placeholder) {
      return;
    }

    const selectedQuestionOption = selectQuestion.options[selectQuestion.selectedIndex];
    const questionIndex = parseInt(selectQuestion.value);
    const chartType = selectChartType.value;

    if (!selectQuestion.value || !chartType) {
      return;
    }

    // Get question data
    const fieldName = selectedQuestionOption.getAttribute("data-field-name");
    const graphStyle = selectedQuestionOption.getAttribute("data-graph-style");
    const trackedValue = selectedQuestionOption.getAttribute("data-tracked-value");
    const trackedValueText = selectedQuestionOption.getAttribute("data-tracked-value-text");
    const questionText = selectedQuestionOption.textContent;
    
    // Get selected recommendation
    let selectedRecommendationRole = null;
    let selectedRecommendationText = null;
    if (selectRecommendation && selectRecommendation.value) {
      const selectedRecOption = selectRecommendation.options[selectRecommendation.selectedIndex];
      selectedRecommendationRole = selectRecommendation.value;
      selectedRecommendationText = selectedRecOption.getAttribute("data-recommendation-text") || "";
    }

    // Get full tracked response data
    const trackedResponsesData = getTrackedResponsesData();
    const trackedResponse = trackedResponsesData[questionIndex];
    if (!trackedResponse) {
      console.error("Tracked response data not found", {
        questionIndex: questionIndex,
        availableIndices: trackedResponsesData.map((_, idx) => idx),
        trackedResponsesDataLength: trackedResponsesData.length,
        reportBuilderData: window.reportBuilderData
      });
      return;
    }

    // Determine actual chart template based on chart type and graph_style
    let chartTemplate = chartType;
    if (chartType === "donut" && graphStyle === "bar") {
      chartTemplate = "bar-chart";
    } else if (chartType === "bar" && graphStyle === "donut") {
      chartTemplate = "donut-chart";
    } else if (chartType === "donut") {
      chartTemplate = "donut-chart";
    } else if (chartType === "bar") {
      chartTemplate = "bar-chart";
    } else if (chartType === "line") {
      chartTemplate = "line-chart";
    } else if (chartType === "traffic-light") {
      chartTemplate = "traffic-light-chart";
    } else if (chartType === "stacked-bar-line") {
      chartTemplate = "stacked-bar-line-graph";
    }

    // Create unique chart ID
    const chartId = `report_chart_${++chartCounter}_${Date.now()}`;

    // Create chart configuration
    const chartConfig = {
      id: chartId,
      questionIndex: questionIndex,
      questionText: questionText,
      fieldName: fieldName,
      chartType: chartTemplate,
      trackedValue: trackedValue,
      trackedValueText: trackedValueText,
      graphStyle: graphStyle,
      trackedResponse: trackedResponse,
      startDate: null,
      endDate: null,
      wardName: null,
      customTitle: null, // Will default to questionText if null
      recommendationRole: selectedRecommendationRole,
      recommendationText: selectedRecommendationText,
    };

    // Add to placed charts
    placedCharts.push(chartConfig);

    // Replace placeholder with chart
    renderChartInCanvas(chartConfig, placeholder);

    // Clear placeholder reference
    currentChartConfigPlaceholder = null;
  }

  /**
   * Populate recommendations dropdown with roles from selected question
   */
  function populateRecommendationsDropdown(questionIndex, selectElement) {
    if (!selectElement) {
      return;
    }
    
    // Clear existing options except the first one
    while (selectElement.options.length > 1) {
      selectElement.remove(1);
    }
    
    // Get tracked responses data
    const trackedResponsesData = getTrackedResponsesData();
    const trackedResponse = trackedResponsesData[questionIndex];
    
    if (!trackedResponse || !trackedResponse.recommendations || !Array.isArray(trackedResponse.recommendations)) {
      return;
    }
    
    // Add options for each recommendation role
    trackedResponse.recommendations.forEach((rec, index) => {
      if (rec.role) {
        const option = document.createElement("option");
        option.value = rec.role;
        option.textContent = rec.role;
        option.setAttribute("data-recommendation-text", rec.recommendation || "");
        selectElement.appendChild(option);
      }
    });
  }

  /**
   * Update inline add button state for specific instance
   */
  function updateInlineAddButtonStateInstance(selectQuestion, selectChartType, addChartBtn) {
    const hasQuestion = selectQuestion.value !== "";
    const hasChartType = selectChartType.value !== "";

    addChartBtn.disabled = !(hasQuestion && hasChartType);
  }

  /**
   * Make an AI summary element editable, keeping pasted content plain text
   * @param {HTMLElement} summaryTextEl - The .chart-ai-summary-text element
   */
  function makeSummaryEditable(summaryTextEl) {
    if (!summaryTextEl) return;

    summaryTextEl.addEventListener("paste", function (e) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData("text/plain");
      if (document.queryCommandSupported && document.queryCommandSupported("insertText")) {
        document.execCommand("insertText", false, text);
      } else {
        const selection = window.getSelection();
        if (selection.rangeCount) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(text));
          range.collapse(false);
        }
      }
    });
  }

  /**
   * Render chart in canvas
   * @param {Object} chartConfig - Chart configuration object
   * @param {HTMLElement} placeholder - Optional placeholder element to replace
   */
  function renderChartInCanvas(chartConfig, placeholder = null) {
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      return;
    }

    // Create chart container
    const chartContainer = document.createElement("div");
    chartContainer.className = "canvas-chart-item";
    chartContainer.id = `chart_container_${chartConfig.id}`;
    chartContainer.setAttribute("data-chart-id", chartConfig.id);

    // Create chart header with controls
    const chartHeader = document.createElement("div");
    chartHeader.className = "chart-item-header";
    const displayTitle = chartConfig.customTitle || chartConfig.questionText;
    
    if (window.ReportBuilderUtils?.generateItemHeaderHtml) {
      chartHeader.innerHTML = window.ReportBuilderUtils.generateItemHeaderHtml({
        itemId: chartConfig.id,
        displayTitle: displayTitle,
        idAttribute: "data-chart-id",
        itemType: "chart",
        removeButtonClass: "btn-remove-chart",
        removeButtonTitle: "Remove chart",
      });
    } else {
      // Fallback if utils not available
      chartHeader.innerHTML = `<div class="chart-item-title"><h4>${displayTitle}</h4></div>`;
    }

    // AI summary container (inline)
    const aiSummary = document.createElement("div");
    aiSummary.className = "chart-ai-summary";
    aiSummary.id = `ai_summary_${chartConfig.id}`;
    aiSummary.style.display = "none";

    // Create chart filters - matching dashboard layout
    const chartFilters = document.createElement("div");
    chartFilters.className = "chart-item-filters month-filter-inputs";

    // Build ward select options
    const wardList = window.reportBuilderData?.wardList || [];
    let wardOptions = '<option value="">All Wards</option>';
    const escapeHtml = window.ReportBuilderUtils?.escapeHtml || function(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    };
    wardList.forEach((wardName) => {
      wardOptions += `<option value="${escapeHtml(wardName)}">${escapeHtml(
        wardName
      )}</option>`;
    });

    chartFilters.innerHTML = `
            <div class="filter-input-group">
                <label for="startMonth_${chartConfig.id}">Start Month:</label>
                <input type="text" id="startMonth_${chartConfig.id}" class="filter-start-date" data-chart-id="${chartConfig.id}" readonly placeholder="Select month..." />
            </div>
            <div class="filter-input-group">
                <label for="endMonth_${chartConfig.id}">End Month:</label>
                <input type="text" id="endMonth_${chartConfig.id}" class="filter-end-date" data-chart-id="${chartConfig.id}" readonly placeholder="Select month..." />
            </div>
            <div class="filter-input-group">
                <label for="wardFilter_${chartConfig.id}">Ward:</label>
                <select id="wardFilter_${chartConfig.id}" class="filter-ward global-ward-filter" data-chart-id="${chartConfig.id}">
                    ${wardOptions}
                </select>
            </div>
            <div class="filter-actions">
                <button type="button" class="btn-ai-summary" data-chart-id="${chartConfig.id}" title="Get AI summary">AI Summary</button>
                <button type="button" class="btn-reset-filter" data-chart-id="${chartConfig.id}">Reset</button>
            </div>
        `;

    // Create chart content area
    const chartContent = document.createElement("div");
    chartContent.className = "chart-item-content";
    chartContent.id = `chart_content_${chartConfig.id}`;
    chartContent.innerHTML =
      '<div class="chart-loading">Loading chart...</div>';

    // Assemble chart container
    chartContainer.appendChild(chartHeader);
    chartContainer.appendChild(chartFilters);
    chartContainer.appendChild(chartContent);
    chartContainer.appendChild(aiSummary);

    // Replace placeholder or add to canvas
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.replaceChild(chartContainer, placeholder);
    } else {
      canvasCharts.appendChild(chartContainer);
    }

    // Setup chart filters event listeners
    setupChartFilters(chartConfig.id);

    // Setup remove button
    const removeBtn = chartContainer.querySelector(".btn-remove-chart");
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils?.showConfirmationModal) {
          window.ReportBuilderUtils.showConfirmationModal({
            title: "Remove Chart",
            message: "Are you sure you want to remove this chart? This action cannot be undone.",
            confirmText: "Remove",
            cancelText: "Cancel",
            onConfirm: function() {
              removeChart(chartConfig.id);
            }
          });
        } else {
          // Fallback if modal not available
          if (confirm("Are you sure you want to remove this chart?")) {
            removeChart(chartConfig.id);
          }
        }
      });
    }

    // Setup move buttons
    if (window.ReportBuilderUtils?.setupMoveButtons) {
      window.ReportBuilderUtils.setupMoveButtons(
        chartContainer,
        chartConfig.id,
        "data-chart-id",
        function() {
          updateChartArrayOrder(chartConfig.id);
        }
      );
    }

    // Setup edit title button
    if (window.ReportBuilderUtils?.setupEditTitleButton) {
      window.ReportBuilderUtils.setupEditTitleButton(
        chartContainer,
        chartConfig.id,
        "chart",
        function(newTitle) {
          chartConfig.customTitle = newTitle;
        }
      );
    }

    // Setup AI summary button
    const aiBtn = chartContainer.querySelector(".btn-ai-summary");
    if (aiBtn) {
      aiBtn.addEventListener("click", async function () {
        const summaryEl = document.getElementById(
          `ai_summary_${chartConfig.id}`
        );
        if (!summaryEl) return;

        // Check if summary already exists and is visible
        const hasSummary = summaryEl.style.display === "block" && 
                          summaryEl.innerHTML.trim() !== "" &&
                          !summaryEl.querySelector(".chart-ai-summary-loading");

        if (hasSummary) {
          // Remove/hide the summary
          summaryEl.style.display = "none";
          summaryEl.innerHTML = "";
          aiBtn.textContent = "AI summary";
          return;
        }

        const chartContainerId = `chart_${chartConfig.id}`;
        const chartData = getChartAiData(chartContainerId);

        // Show the container when generating summary
        summaryEl.style.display = "block";

        if (!chartData) {
          summaryEl.innerHTML = `<div class="chart-ai-summary-error">Chart data is not ready yet. Please try again in a moment.</div>`;
          aiBtn.textContent = "Remove summary";
          return;
        }

        const promptString = buildAiPrompt({ chartConfig, chartData });

        // UI states
        aiBtn.disabled = true;
        aiBtn.textContent = "Summarizing...";
        summaryEl.innerHTML = `<div class="chart-ai-summary-loading">Generating summary…</div>`;

        const escapeHtml = window.ReportBuilderUtils?.escapeHtml || function(text) {
          const div = document.createElement("div");
          div.textContent = text;
          return div.innerHTML;
        };
        
        try {
          const analysis = await requestAiSummary(promptString);
          summaryEl.innerHTML = `<div class="chart-ai-summary-text" contenteditable="true" spellcheck="true" title="Click to edit summary">${escapeHtml(
            analysis
          )}</div>`;
          makeSummaryEditable(summaryEl.querySelector(".chart-ai-summary-text"));
          aiBtn.textContent = "Remove summary";
        } catch (err) {
          console.error("AI summary error:", err);
          summaryEl.innerHTML = `<div class="chart-ai-summary-error">${escapeHtml(
            err.message || "Failed to generate summary."
          )}</div>`;
          aiBtn.textContent = "Remove summary";
        } finally {
          aiBtn.disabled = false;
        }
      });
    }

    // Load chart via AJAX
    loadChartViaAjax(chartConfig);
  }

  /**
   * Setup chart filters event listeners
   */
  function setupChartFilters(chartId) {
    const startDateInput = document.querySelector(`#startMonth_${chartId}`);
    const endDateInput = document.querySelector(`#endMonth_${chartId}`);
    const wardSelect = document.querySelector(`#wardFilter_${chartId}`);
    const resetBtn = document.querySelector(
      `.btn-reset-filter[data-chart-id="${chartId}"]`
    );

    // Initialize month pickers using jQuery UI datepicker (same as dashboard)
    if (typeof jQuery !== "undefined" && jQuery.fn.datepicker) {
      // Initialize start month picker
      if (startDateInput) {
        jQuery(startDateInput).datepicker({
          changeMonth: true,
          changeYear: true,
          showButtonPanel: true,
          dateFormat: "yy-mm",
          yearRange: "-10:+1",
          onClose: function (dateText, inst) {
            const month = jQuery(
              "#ui-datepicker-div .ui-datepicker-month :selected"
            ).val();
            const year = jQuery(
              "#ui-datepicker-div .ui-datepicker-year :selected"
            ).val();

            if (month !== undefined && year !== undefined) {
              const monthIndex = parseInt(month);
              const displayValue = formatMonthDisplay(year, monthIndex);
              const apiValue = formatMonthForAPI(year, monthIndex);

              jQuery(this).val(displayValue);
              jQuery(this).attr("data-value", apiValue);

              // Auto-apply filters
              applyChartFilters(chartId);
            }
          },
          beforeShow: function (input, inst) {
            const apiValue = jQuery(this).attr("data-value");
            if (apiValue && apiValue.length > 0) {
              const parts = apiValue.split("-");
              if (parts.length === 2) {
                const year = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1;
                jQuery(this).datepicker(
                  "option",
                  "defaultDate",
                  new Date(year, month, 1)
                );
                jQuery(this).datepicker("setDate", new Date(year, month, 1));
              }
            }

            setTimeout(function () {
              jQuery("#ui-datepicker-div .ui-datepicker-calendar").hide();
              jQuery("#ui-datepicker-div").position({
                my: "center top",
                at: "center bottom",
                of: jQuery(input),
              });
            }, 1);
          },
        });

        jQuery(startDateInput).focus(function () {
          setTimeout(function () {
            jQuery(".ui-datepicker-calendar").hide();
          }, 1);
        });
      }

      // Initialize end month picker
      if (endDateInput) {
        jQuery(endDateInput).datepicker({
          changeMonth: true,
          changeYear: true,
          showButtonPanel: true,
          dateFormat: "yy-mm",
          yearRange: "-10:+1",
          onClose: function (dateText, inst) {
            const month = jQuery(
              "#ui-datepicker-div .ui-datepicker-month :selected"
            ).val();
            const year = jQuery(
              "#ui-datepicker-div .ui-datepicker-year :selected"
            ).val();

            if (month !== undefined && year !== undefined) {
              const monthIndex = parseInt(month);
              const displayValue = formatMonthDisplay(year, monthIndex);
              const apiValue = formatMonthForAPI(year, monthIndex);

              jQuery(this).val(displayValue);
              jQuery(this).attr("data-value", apiValue);

              // Auto-apply filters
              applyChartFilters(chartId);
            }
          },
          beforeShow: function (input, inst) {
            const apiValue = jQuery(this).attr("data-value");
            if (apiValue && apiValue.length > 0) {
              const parts = apiValue.split("-");
              if (parts.length === 2) {
                const year = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1;
                jQuery(this).datepicker(
                  "option",
                  "defaultDate",
                  new Date(year, month, 1)
                );
                jQuery(this).datepicker("setDate", new Date(year, month, 1));
              }
            }

            setTimeout(function () {
              jQuery("#ui-datepicker-div .ui-datepicker-calendar").hide();
              jQuery("#ui-datepicker-div").position({
                my: "center top",
                at: "center bottom",
                of: jQuery(input),
              });
            }, 1);
          },
        });

        jQuery(endDateInput).focus(function () {
          setTimeout(function () {
            jQuery(".ui-datepicker-calendar").hide();
          }, 1);
        });
      }
    }

    // Auto-apply filters when ward filter changes
    if (wardSelect) {
      wardSelect.addEventListener("change", function () {
        applyChartFilters(chartId);
      });
    }

    // Reset button
    if (resetBtn) {
      resetBtn.addEventListener("click", function () {
        if (startDateInput) {
          startDateInput.value = "";
          startDateInput.removeAttribute("data-value");
        }
        if (endDateInput) {
          endDateInput.value = "";
          endDateInput.removeAttribute("data-value");
        }
        if (wardSelect) {
          wardSelect.value = "";
        }

        // Reload chart with no filters
        const chartConfig = placedCharts.find((c) => c.id === chartId);
        if (chartConfig) {
          chartConfig.startDate = null;
          chartConfig.endDate = null;
          chartConfig.wardName = null;
          loadChartViaAjax(chartConfig);
        }
      });
    }
  }

  /**
   * Apply filters to a specific chart
   */
  function applyChartFilters(chartId) {
    const chartConfig = placedCharts.find((c) => c.id === chartId);
    if (!chartConfig) {
      return;
    }

    const startDateInput = document.querySelector(`#startMonth_${chartId}`);
    const endDateInput = document.querySelector(`#endMonth_${chartId}`);
    const wardSelect = document.querySelector(`#wardFilter_${chartId}`);

    // Get filter values
    const startDate = startDateInput
      ? startDateInput.getAttribute("data-value") || null
      : null;
    const endDate = endDateInput
      ? endDateInput.getAttribute("data-value") || null
      : null;
    const wardName = wardSelect ? wardSelect.value || null : null;

    // Validate date order
    if (startDate && endDate && startDate > endDate) {
      alert("Start month must be before or equal to end month.");
      return;
    }

    // Update chart config
    chartConfig.startDate = startDate;
    chartConfig.endDate = endDate;
    chartConfig.wardName = wardName;

    // Reload chart with new filters
    loadChartViaAjax(chartConfig);
  }

  /**
   * Format month for display (e.g., "Jan, 2025")
   */
  function formatMonthDisplay(year, month) {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return monthNames[month] + ", " + year;
  }

  /**
   * Format month for API (e.g., "2025-01")
   */
  function formatMonthForAPI(year, month) {
    const monthNum = parseInt(month) + 1;
    const monthStr = monthNum < 10 ? "0" + monthNum : monthNum.toString();
    return year + "-" + monthStr;
  }

  /**
   * Load chart via AJAX
   */
  async function loadChartViaAjax(chartConfig) {
    const chartContent = document.getElementById(
      `chart_content_${chartConfig.id}`
    );
    if (!chartContent) {
      return;
    }

    // Show loading state
    chartContent.innerHTML =
      '<div class="chart-loading">Loading chart...</div>';

    try {
      // Prepare tracked response data for AJAX
      const trackedResponseData = {
        group_heading: chartConfig.questionText,
        tracked_response: chartConfig.fieldName,
        tracked_value: chartConfig.trackedValue,
        tracked_value_text: chartConfig.trackedValueText,
        graph_style: chartConfig.graphStyle,
        donut_response_mappings: {},
        donut_response_colors: {},
        x_axis_label: "",
        y_axis_label: "",
        targets: {},
        graph_headings: chartConfig.trackedResponse.graph_headings || {},
        calculation: chartConfig.trackedResponse.calculation || {},
      };

      // Extract donut response mappings
      if (
        chartConfig.trackedResponse.donut_responses &&
        chartConfig.trackedResponse.donut_responses.tracked_donut_responses
      ) {
        chartConfig.trackedResponse.donut_responses.tracked_donut_responses.forEach(
          (item) => {
            if (item.donut_response) {
              const parts = item.donut_response.split("|");
              if (parts.length === 2) {
                trackedResponseData.donut_response_mappings[parts[0].trim()] =
                  parts[1].trim();
                if (item.colour) {
                  trackedResponseData.donut_response_colors[parts[0].trim()] =
                    item.colour;
                }
              }
            }
          }
        );
      }

      // Extract bar graph labels
      if (chartConfig.trackedResponse.bar_graph_labels) {
        trackedResponseData.x_axis_label =
          chartConfig.trackedResponse.bar_graph_labels.x_axis_label || "";
        trackedResponseData.y_axis_label =
          chartConfig.trackedResponse.bar_graph_labels.y_axis_label || "";
      }

      // Extract targets
      if (chartConfig.trackedResponse.targets) {
        trackedResponseData.targets = chartConfig.trackedResponse.targets;
      }

      // Make AJAX request to load chart template
      const formData = new FormData();
      formData.append("action", "load_chart_template");

      // Check if nonce exists
      if (!window.reportBuilderData || !window.reportBuilderData.nonce) {
        throw new Error(
          "Report builder data not loaded. Please refresh the page."
        );
      }

      formData.append("nonce", window.reportBuilderData.nonce);
      formData.append("chart_type", chartConfig.chartType);
      formData.append("chart_id", `chart_${chartConfig.id}`);
      formData.append("tracked_field", chartConfig.fieldName);
      formData.append("tracked_response", JSON.stringify(trackedResponseData));
      formData.append("use_batch_api", "true"); // Shared batch request via ChartDataManager
      formData.append("enable_date_filter", "false"); // We handle filters ourselves
      formData.append("start_date", chartConfig.startDate || "");
      formData.append("end_date", chartConfig.endDate || "");
      formData.append("ward_name", chartConfig.wardName || "");
      formData.append("recommendation_role", chartConfig.recommendationRole || "");
      formData.append("recommendation_text", chartConfig.recommendationText || "");

      const ajaxUrl =
        window.reportBuilderData?.ajaxurl ||
        (typeof ajaxurl !== "undefined" ? ajaxurl : "/wp-admin/admin-ajax.php");

      const response = await fetch(ajaxUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("AJAX response error:", response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        const errorMsg =
          result.data?.message || "Failed to load chart template";
        console.error("Chart template load failed:", errorMsg);
        throw new Error(errorMsg);
      }

      // Insert HTML into chart content
      chartContent.innerHTML = result.data.html;

      // Execute any scripts in the inserted HTML
      // Scripts inserted via innerHTML don't execute automatically
      const scripts = chartContent.querySelectorAll("script");
      scripts.forEach((oldScript) => {
        const newScript = document.createElement("script");
        if (oldScript.src) {
          newScript.src = oldScript.src;
        } else {
          newScript.textContent = oldScript.textContent;
        }
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });

      // The template seeds its first load from initial_filters, so nothing else to do here.
    } catch (error) {
      console.error("Error loading chart:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        chartConfig: chartConfig,
      });
      const escapeHtml = window.ReportBuilderUtils?.escapeHtml || function(text) {
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
      };
      
      chartContent.innerHTML = `
                <div class="chart-error">
                    <p>Error loading chart data: ${escapeHtml(
                      error.message
                    )}</p>
                    <p style="font-size: 0.875rem; color: #666; margin-top: 0.5rem;">Check the browser console for more details.</p>
                    <button class="btn-retry-chart" data-chart-id="${
                      chartConfig.id
                    }">Retry</button>
                </div>
            `;

      // Setup retry button
      const retryBtn = chartContent.querySelector(".btn-retry-chart");
      if (retryBtn) {
        retryBtn.addEventListener("click", function () {
          loadChartViaAjax(chartConfig);
        });
      }
    }
  }

  /**
   * Update chart array order to match DOM order
   */
  function updateChartArrayOrder(chartId) {
    if (window.ReportBuilderUtils?.updateArrayOrder) {
      window.ReportBuilderUtils.updateArrayOrder(
        placedCharts,
        chartId,
        "chart_container_",
        "data-chart-id"
      );
    }
  }

  /**
   * Remove chart from canvas
   */
  function removeChart(chartId) {
    // Remove from placed charts array
    placedCharts = placedCharts.filter((c) => c.id !== chartId);

    // Remove from DOM
    const chartContainer = document.getElementById(
      `chart_container_${chartId}`
    );
    if (chartContainer) {
      chartContainer.remove();
    }

    // Show empty message if no charts left
    if (placedCharts.length === 0) {
      if (window.ReportBuilderUtils?.toggleEmptyMessage) {
        window.ReportBuilderUtils.toggleEmptyMessage(true);
      }
    }
  }

  /**
   * Get chart type label
   */
  function getChartTypeLabel(chartType) {
    const labels = {
      "stacked-bar-line-graph": "Bar-Line Chart",
      "donut-chart": "Donut Chart",
      "bar-chart": "Bar Chart",
      "line-chart": "Line Chart",
      "traffic-light-chart": "Traffic Light Chart",
    };
    return labels[chartType] || chartType;
  }

  // Export functions for external use
  window.ReportBuilderChart = {
    addChartPlaceholder: addChartPlaceholder,
    removeChart: removeChart,
    getPlacedCharts: function() { return placedCharts; },
  };

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPlaceChart);
  } else {
    initPlaceChart();
  }
})();
